<template>
    <vx-expandable-card>
        <template v-slot:header>
            <span class="capitalize">{{ props.type }}</span>
        </template>
        <template v-slot:headerRight>
            <vx-badge :color="statusColor">
                {{ statusText }}
            </vx-badge>
        </template>
        <template v-slot:content>
            <div class="space-y-4">
                <div>
                    <vx-label :for="`${props.type}-url`">SFTP URL</vx-label>
                    <vx-input
                        :id="`${props.type}-url`"
                        v-model="formData.url"
                        type="text"
                        placeholder="sftp://example.com/path/to/file.csv"
                        :error="displayFirstError(validate.url.$errors)"
                        @blur="validate.url.$touch()"
                        data-test="url-input"
                    />
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-username`">Username</vx-label>
                    <vx-input
                        :id="`${props.type}-username`"
                        v-model="formData.username"
                        type="text"
                        placeholder="Username"
                        :error="displayFirstError(validate.username.$errors)"
                        @blur="validate.username.$touch()"
                        data-test="username-input"
                    />
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-password`">Password</vx-label>
                    <vx-input
                        :id="`${props.type}-password`"
                        :key="passwordInputKey"
                        v-model="formData.password"
                        type="password"
                        :placeholder="
                            props.existing?.has_password && !isPasswordTouched ? '••••••••' : 'Enter password'
                        "
                        :togglePassword="isPasswordTouched"
                        :error="displayFirstError(validate.password.$errors)"
                        @blur="validate.password.$touch()"
                        @input="isPasswordTouched = true"
                        data-test="password-input"
                    />
                    <vx-button
                        v-if="props.existing?.has_password && !isPasswordDeleted"
                        type="button"
                        color="danger"
                        size="sm"
                        class="mt-2"
                        @click="deletePassword"
                        data-test="delete-password-button"
                    >
                        Delete Password
                    </vx-button>
                </div>

                <div v-if="formData.url">
                    <vx-label :for="`${props.type}-ssh-key`">SSH Private Key</vx-label>
                    <input
                        type="file"
                        :id="`${props.type}-ssh-key-file`"
                        ref="sshKeyFileInput"
                        class="opacity-0 absolute w-1 h-1"
                        accept=".pem,.ppk,.key,text/plain"
                        aria-hidden="true"
                        @change="handleSshKeyFileUpload"
                        data-test="ssh-key-file-input"
                    />
                    <div class="space-y-2">
                        <div class="flex gap-2">
                            <vx-button
                                type="button"
                                color="muted"
                                size="sm"
                                outline
                                aria-label="Upload SSH private key file"
                                @click="triggerSshKeyFileInput"
                                data-test="upload-ssh-key-button"
                            >
                                {{
                                    uploadedSshKeyFileName ||
                                    (props.existing?.has_ssh_private_key && !isSshKeyTouched ? 'Re-Upload' : 'Upload')
                                }}
                                <font-awesome-icon
                                    widthAuto
                                    :icon="
                                        props.existing?.has_ssh_private_key && !isSshKeyTouched ? faRotate : faUpload
                                    "
                                />
                            </vx-button>
                            <vx-button
                                v-if="uploadedSshKeyFileName"
                                type="button"
                                color="muted"
                                size="sm"
                                outline
                                aria-label="Clear uploaded SSH key"
                                @click="clearUploadedSshKey"
                                data-test="clear-ssh-key-button"
                            >
                                <font-awesome-icon widthAuto :icon="faXmark" />
                            </vx-button>
                        </div>
                        <div v-if="displayFirstError(validate.ssh_private_key.$errors)" class="text-rose-800 text-xs">
                            {{ displayFirstError(validate.ssh_private_key.$errors) }}
                        </div>
                    </div>
                    <vx-button
                        v-if="props.existing?.has_ssh_private_key && !isSshKeyDeleted"
                        type="button"
                        color="danger"
                        size="sm"
                        class="mt-2"
                        @click="deleteSshKey"
                        data-test="delete-ssh-key-button"
                    >
                        Delete SSH Key
                    </vx-button>
                </div>

                <div v-if="formData.url" class="mt-6 flex items-center gap-4">
                    <vx-button
                        type="button"
                        :disabled="processing"
                        :processing="processing"
                        @click="handleSave"
                        data-test="save-button"
                    >
                        {{ props.existing?.url ? 'Update' : 'Save' }}
                    </vx-button>

                    <vx-button
                        v-if="props.existing?.url"
                        type="button"
                        color="danger"
                        :disabled="processing"
                        @click="handleDelete"
                        data-test="delete-button"
                    >
                        Delete
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { VxExpandableCard, VxLabel, VxInput, VxButton, VxBadge, VxSwal } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import teamsClient from '~/services/teamsClient';
import type { FtpPullDataTypeConfig, FtpPullExistingConfig } from '~/types/ftp-pull.types';
import { catchRequestError } from '~/utils/request';
import { displayFirstError, noSftpEmbeddedCredentials, startsWithSftp, validSftpUrlFormat } from '~/utils/validation';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faUpload, faRotate, faXmark } from '@fortawesome/pro-solid-svg-icons';

interface Props {
    type: 'contacts' | 'conversions' | 'groups';
    existing?: FtpPullExistingConfig;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    configUpdated: [];
}>();

const toasted = useToasted();

const statusColor = computed(() => {
    if (props.existing?.url) {
        return 'success';
    }
    return 'slate';
});

const statusText = computed(() => {
    if (props.existing?.url) {
        return 'Active';
    }
    return 'Not Active';
});

const formData = ref<FtpPullDataTypeConfig>({
    url: props.existing?.url || '',
    username: props.existing?.username || '',
    password: '',
    ssh_private_key: '',
});

const isPasswordTouched = ref(false);
// Remounting is the only way to hide a password revealed with VxInput's toggle.
const passwordInputKey = ref(0);
const isSshKeyTouched = ref(false);
const processing = ref(false);
const isPasswordDeleted = ref(false);
const isSshKeyDeleted = ref(false);
const originalUrl = ref(props.existing?.url || '');
const sshKeyFileInput = ref<HTMLInputElement | null>(null);
const uploadedSshKeyFileName = ref('');

watch(
    () => props.existing,
    (newExisting) => {
        formData.value.url = newExisting?.url || '';
        formData.value.username = newExisting?.username || '';
        formData.value.password = '';
        formData.value.ssh_private_key = '';
        originalUrl.value = newExisting?.url || '';
        isPasswordTouched.value = false;
        passwordInputKey.value += 1;
        isSshKeyTouched.value = false;
        uploadedSshKeyFileName.value = '';
        // Only reset deletion flags if credentials actually exist in new config
        isPasswordDeleted.value = !newExisting?.has_password;
        isSshKeyDeleted.value = !newExisting?.has_ssh_private_key;
        validate.value.$reset();
    },
    { deep: true }
);

watch(
    () => formData.value.password,
    (newValue) => {
        if (newValue && isPasswordDeleted.value) {
            isPasswordDeleted.value = false;
        }
    }
);

watch(
    () => formData.value.ssh_private_key,
    (newValue) => {
        if (newValue && isSshKeyDeleted.value) {
            isSshKeyDeleted.value = false;
        }
    }
);

const requireAuthMethod = helpers.withMessage('At least a password or SSH key is required.', () => {
    if (!formData.value.url) {
        return true;
    }
    const hasPassword = (formData.value.password || props.existing?.has_password) && !isPasswordDeleted.value;
    const hasSshKey = (formData.value.ssh_private_key || props.existing?.has_ssh_private_key) && !isSshKeyDeleted.value;
    return !!(hasPassword || hasSshKey);
});

const noPassphrasedOpenSshKey = helpers.withMessage(
    'Passphrase-protected OpenSSH keys are not supported due to performance issues. Please provide an unencrypted SSH key.',
    (value: string) => {
        if (!value?.includes('-----BEGIN OPENSSH PRIVATE KEY-----')) {
            return true;
        }

        try {
            const content = value.replace(
                /-----BEGIN OPENSSH PRIVATE KEY-----|-----END OPENSSH PRIVATE KEY-----|\s/g,
                ''
            );
            return !atob(content).includes('bcrypt');
        } catch {
            return true;
        }
    }
);

const validPrivateKeyFormat = helpers.withMessage(
    'The uploaded file does not appear to be a valid SSH private key. Supported formats: PKCS1, PKCS8, OpenSSH, PuTTY.',
    (value: string) => {
        if (!value) {
            return true;
        }

        // Supported formats: PKCS1, PKCS8, OpenSSH, PuTTY
        // Supported key types: RSA, DSA, ECDSA, Ed25519
        const privateKeyPatterns = [
            '-----BEGIN OPENSSH PRIVATE KEY-----', // OpenSSH format
            '-----BEGIN RSA PRIVATE KEY-----', // PKCS1 RSA
            '-----BEGIN DSA PRIVATE KEY-----', // PKCS1 DSA
            '-----BEGIN EC PRIVATE KEY-----', // PKCS1 ECDSA
            '-----BEGIN PRIVATE KEY-----', // PKCS8
            '-----BEGIN ENCRYPTED PRIVATE KEY-----', // PKCS8 encrypted
            'PuTTY-User-Key-File-2', // PuTTY format v2
            'PuTTY-User-Key-File-3', // PuTTY format v3
        ];

        return privateKeyPatterns.some((pattern) => value.includes(pattern));
    }
);

const rules = computed(() => ({
    url: { startsWithSftp, noSftpEmbeddedCredentials, validSftpUrlFormat },
    username: {
        required: helpers.withMessage('Username is required when a URL is provided.', (value: string) => {
            if (!formData.value.url) {
                return true;
            }

            return !!value;
        }),
    },
    password: { requireAuthMethod },
    ssh_private_key: { requireAuthMethod, validPrivateKeyFormat, noPassphrasedOpenSshKey },
}));

const validate = useVuelidate(rules, formData);

const triggerSshKeyFileInput = () => {
    if (sshKeyFileInput.value) {
        sshKeyFileInput.value.click();
    }
};

const handleSshKeyFileUpload = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) {
        return;
    }

    // Validate file size (max 64 KB for SSH keys)
    const maxSizeInKB = 64;
    const fileSizeInKB = file.size / 1024;
    if (fileSizeInKB > maxSizeInKB) {
        toasted.error(`SSH key file is too large. Maximum size is ${maxSizeInKB} KB.`);
        target.value = '';
        return;
    }

    try {
        // Read the file content as text
        const fileContent = await file.text();

        // Store the content in formData
        formData.value.ssh_private_key = fileContent;
        isSshKeyTouched.value = true;
        uploadedSshKeyFileName.value = file.name;

        // Trigger validation
        validate.value.ssh_private_key.$touch();

        // Clear the file input
        target.value = '';
    } catch {
        toasted.error('Failed to read SSH key file. Please try again.');
        target.value = '';
    }
};

const clearUploadedSshKey = () => {
    formData.value.ssh_private_key = '';
    uploadedSshKeyFileName.value = '';
    isSshKeyTouched.value = true;
    validate.value.ssh_private_key.$touch();
};

const handleSave = async () => {
    if (props.existing?.url && formData.value.url !== originalUrl.value) {
        const result = await VxSwal.fire({
            title: 'Confirm URL Change',
            text: 'Make sure to update the username, password, and/or SSH key.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, continue',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) {
            return;
        }
    }

    const isValid = await validate.value.$validate();
    if (!isValid) {
        return;
    }

    processing.value = true;

    try {
        const payload: Record<string, FtpPullDataTypeConfig> = {
            [props.type]: {
                url: formData.value.url,
                username: formData.value.username,
                password: isPasswordDeleted.value ? '__DELETE__' : formData.value.password,
                ssh_private_key: isSshKeyDeleted.value ? '__DELETE__' : formData.value.ssh_private_key,
            },
        };

        await teamsClient.saveFtpPullConfig(Spark.state.currentTeam.id, payload);
        toasted.success(
            `${props.type.charAt(0).toUpperCase() + props.type.slice(1)} configuration saved successfully.`
        );

        formData.value.password = '';
        formData.value.ssh_private_key = '';
        isPasswordTouched.value = false;
        passwordInputKey.value += 1;
        isSshKeyTouched.value = false;
        uploadedSshKeyFileName.value = '';
        originalUrl.value = formData.value.url;
        validate.value.$reset();

        // Notify parent to reload config (watcher will reset deletion flags based on new config)
        emit('configUpdated');
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        processing.value = false;
    }
};

const handleDelete = async () => {
    const result = await VxSwal.fire({
        title: 'Delete Configuration',
        text: `Are you sure you want to delete the ${props.type} configuration?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'danger',
    });

    if (!result.isConfirmed) {
        return;
    }

    processing.value = true;

    try {
        await teamsClient.deleteFtpPullConfig(Spark.state.currentTeam.id, props.type);
        toasted.success(
            `${props.type.charAt(0).toUpperCase() + props.type.slice(1)} configuration deleted successfully.`
        );

        emit('configUpdated');
    } catch (error) {
        toasted.error(catchRequestError(error));
    } finally {
        processing.value = false;
    }
};

const deletePassword = () => {
    isPasswordDeleted.value = true;
    isPasswordTouched.value = true;
    formData.value.password = '';
};

const deleteSshKey = () => {
    isSshKeyDeleted.value = true;
    isSshKeyTouched.value = true;
    formData.value.ssh_private_key = '';
    uploadedSshKeyFileName.value = '';
};
</script>
